/**
 * Central Configuration Management
 * All app configuration, environment variables, and constants in one place
 */

import { env } from './env';
import { constants } from './constants';
import { features } from './features';

// Combine all configurations
const config = {
  // Environment configuration
  env: env,

  // Application constants
  constants: constants,

  // Feature flags
  features: features,

  // API Configuration
  api: {
    // baseUrl removed as we use Supabase directly
    supabase: {
      url: env.SUPABASE_URL,
      anonKey: env.SUPABASE_ANON_KEY,
    },
    notion: {
      token: env.NOTION_TOKEN,
      databaseId: env.NOTION_DATABASE_ID,
    },
  },

  // App Configuration
  app: {
    name: 'BLL Pro',
    version: process.env.REACT_APP_VERSION || '1.0.0',
    environment: env.NODE_ENV,
    isDevelopment: env.isDevelopment,
    isProduction: env.isProduction,
    isTest: env.isTest,
  },

  // Auth Configuration
  auth: {
    redirectUrl: env.REDIRECT_URL,
    sessionTimeout: 60 * 60 * 1000, // 1 hour
    tokenRefreshThreshold: 10 * 60, // 10 minutes before expiry (increased from 5)
    storageKey: 'supabase.auth.token',
  },

  // UI Configuration
  ui: {
    theme: {
      mode: 'light', // 'light' | 'dark' | 'auto'
      primaryColor: '#1976d2',
      secondaryColor: '#dc004e',
    },
    animation: {
      enabled: true,
      duration: 300,
    },
    pagination: {
      defaultPageSize: 20,
      pageSizeOptions: [10, 20, 50, 100],
    },
  },

  // Test Configuration
  test: {
    mbe: {
      secondsPerQuestion: 108, // 1.8 minutes per question
      maxQuestions: 200,
      passingScore: 70,
    },
    mee: {
      minutesPerEssay: 30,
      maxEssays: 6,
      passingScore: 60,
    },
    mpt: {
      minutesPerTask: 90,
      maxTasks: 2,
      passingScore: 60,
    },
  },

  // Storage Configuration
  storage: {
    localStorage: {
      prefix: 'bll_pro_',
      expirationDays: 30,
    },
    sessionStorage: {
      prefix: 'bll_pro_session_',
    },
  },

  // Logging Configuration
  logging: {
    enabled: env.isDevelopment,
    level: env.isDevelopment ? 'debug' : 'error',
    console: env.isDevelopment,
    remote: env.isProduction,
  },

  // Performance Configuration
  performance: {
    debounceDelay: 300,
    throttleDelay: 1000,
    lazyLoadThreshold: 0.1,
    cacheTimeout: 15 * 60 * 1000, // 15 minutes
  },

  // Validation Rules
  validation: {
    password: {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: false,
    },
    username: {
      minLength: 3,
      maxLength: 20,
      pattern: /^[a-zA-Z0-9_]+$/,
    },
    email: {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    },
  },

  // SEO Configuration
  seo: {
    defaultTitle: 'BLL Pro - Bar Exam Preparation',
    titleTemplate: '%s | BLL Pro',
    defaultDescription: 'Comprehensive bar exam preparation platform',
    defaultKeywords: ['bar exam', 'MBE', 'MEE', 'MPT', 'legal education'],
  },
};

// Helper functions
export const getConfig = (path) => {
  const keys = path.split('.');
  let value = config;

  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      console.warn(`Config path "${path}" not found`);
      return undefined;
    }
  }

  return value;
};

export const isFeatureEnabled = (featureName) => {
  return config.features[featureName] === true;
};

// getApiUrl removed as we use Supabase directly

export const getStorageKey = (key, isSession = false) => {
  const prefix = isSession
    ? config.storage.sessionStorage.prefix
    : config.storage.localStorage.prefix;
  return `${prefix}${key}`;
};

// Export individual configs for backward compatibility
export { env, constants, features };
export const { api, app, auth, ui, test, storage, logging, performance, validation, seo } = config;

// Default export
export default config;